import React, { useState, useEffect } from "react";
import api from "../../utils/api";
import "./ReferralTracker.css";

const ReferralTracker = () => {
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReferrer, setSelectedReferrer] = useState(null);
  const [referrerDetails, setReferrerDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/admin/referrals");
      setReferralData(res.data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching referral data:", err);
      setError("Failed to load referral data. Please try again.");
      setLoading(false);
    }
  };

  const fetchReferrerDetails = async (referralCode) => {
    try {
      setDetailsLoading(true);
      const res = await api.get(`/api/admin/referrals/${referralCode}`);
      setReferrerDetails(res.data);
      setDetailsLoading(false);
    } catch (err) {
      console.error("Error fetching referrer details:", err);
      setError("Failed to load referrer details. Please try again.");
      setDetailsLoading(false);
    }
  };

  const handleReferrerSelect = (referrer) => {
    setSelectedReferrer(referrer);
    fetchReferrerDetails(referrer.referralCode);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return <div className="loading">Loading referral data...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="referral-tracker-container">
      <h2>Referral Tracker</h2>

      <div className="referral-stats-cards">
        <div className="stat-card">
          <h3>Total Referrals</h3>
          <p className="stat-number">{referralData.stats.totalReferrals}</p>
        </div>
        <div className="stat-card">
          <h3>Active Referrers</h3>
          <p className="stat-number">{referralData.stats.totalReferrers}</p>
        </div>
        <div className="stat-card">
          <h3>Bonuses Claimed</h3>
          <p className="stat-number">{referralData.stats.totalBonusesClaimed}</p>
        </div>
        <div className="stat-card">
          <h3>Total Bonus Amount</h3>
          <p className="stat-number">₹{referralData.stats.totalBonusAmount}</p>
        </div>
      </div>

      <div className="referral-data-container">
        <div className="referrers-section">
          <h3>Top Referrers</h3>
          <table className="referrers-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Referral Code</th>
                <th>Referrals</th>
                <th>Balance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {referralData.referrerStats.map((referrer) => (
                <tr 
                  key={referrer._id}
                  className={selectedReferrer && selectedReferrer._id === referrer._id ? "selected-row" : ""}
                >
                  <td>{referrer.name}</td>
                  <td>{referrer.email}</td>
                  <td>
                    <span className={`role-badge ${referrer.role}`}>
                      {referrer.role.charAt(0).toUpperCase() + referrer.role.slice(1)}
                    </span>
                  </td>
                  <td>
                    <code className="referral-code">{referrer.referralCode}</code>
                  </td>
                  <td className="referral-count">{referrer.referralCount}</td>
                  <td>₹{referrer.s30Balance || 0}</td>
                  <td>
                    <button 
                      className="view-details-btn"
                      onClick={() => handleReferrerSelect(referrer)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
              {referralData.referrerStats.length === 0 && (
                <tr>
                  <td colSpan="7" className="no-data">No referrers found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="referred-users-section">
          <h3>Recently Referred Users</h3>
          <table className="referred-users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Referred By</th>
                <th>Joined Date</th>
                <th>Bonus Claimed</th>
              </tr>
            </thead>
            <tbody>
              {referralData.referredUsers.map((user) => (
                <tr key={user._id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`role-badge ${user.role}`}>
                      {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                    </span>
                  </td>
                  <td>
                    <code className="referral-code">{user.referredBy}</code>
                  </td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>
                    <span className={`bonus-status ${user.referralBonusClaimed ? "claimed" : "unclaimed"}`}>
                      {user.referralBonusClaimed ? "Yes" : "No"}
                    </span>
                  </td>
                </tr>
              ))}
              {referralData.referredUsers.length === 0 && (
                <tr>
                  <td colSpan="6" className="no-data">No referred users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedReferrer && (
        <div className="referrer-details-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Referrer Details</h3>
              <button className="close-button" onClick={() => setSelectedReferrer(null)}>×</button>
            </div>
            
            {detailsLoading ? (
              <div className="loading">Loading details...</div>
            ) : referrerDetails ? (
              <div className="modal-body">
                <div className="referrer-info">
                  <h4>{referrerDetails.referrer.name}</h4>
                  <p><strong>Email:</strong> {referrerDetails.referrer.email}</p>
                  <p><strong>Role:</strong> {referrerDetails.referrer.role}</p>
                  <p><strong>Referral Code:</strong> <code>{referrerDetails.referrer.referralCode}</code></p>
                  <p><strong>Balance:</strong> ₹{referrerDetails.referrer.s30Balance || 0}</p>
                  <p><strong>Total Referred:</strong> {referrerDetails.stats.totalReferred}</p>
                  <p><strong>Bonuses Claimed:</strong> {referrerDetails.stats.bonusesClaimed}</p>
                </div>
                
                <h4>Referred Users</h4>
                <table className="referred-users-detail-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Joined Date</th>
                      <th>Bonus Claimed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrerDetails.referredUsers.map((user) => (
                      <tr key={user._id}>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`role-badge ${user.role}`}>
                            {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                          </span>
                        </td>
                        <td>{formatDate(user.createdAt)}</td>
                        <td>
                          <span className={`bonus-status ${user.referralBonusClaimed ? "claimed" : "unclaimed"}`}>
                            {user.referralBonusClaimed ? "Yes" : "No"}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {referrerDetails.referredUsers.length === 0 && (
                      <tr>
                        <td colSpan="5" className="no-data">No referred users found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="error-message">Failed to load referrer details</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralTracker;
